import styled, { css } from 'styled-components';

import { size } from '../../layout/helpers';
import { fadeIn, noTransform } from '../../layout/keyframes';
import { ButtonReset } from '../../layout/reset';

const iconSize = (props: any) => props.theme.font.size.large;
const headingSize = 'var(--font-size-h3)';
const headingLineHeight = (props: any) => props.theme.font.lineHeight.main;

export const Wrapper = styled.div.attrs(() => ({
  role: 'dialog',
  'aria-modal': 'true',
})).withConfig({ componentId: 'sc-qdyfpo-0' })`
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    min-height: 100%;
    text-align: left;
    white-space: normal;
    overflow: auto;
    overscroll-behavior: contain;
    z-index: ${(props) => props.theme.zIndex.modal};
`;

export const Backdrop = styled.div.attrs(() => ({
  role: 'button',
})).withConfig({ componentId: 'sc-qdyfpo-1' })`
    display: table;
    width: 100%;
    height: 100%;
    overflow: hidden;
    isolation: isolate;

    &::before {
        content: "";
        position: fixed;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        background-color: rgba(var(--c-overlay-rgb), 0.2);
        z-index: -1;
        animation: ${fadeIn} 400ms ease;
    }
`;

export const Center = styled.div.withConfig({ componentId: 'sc-qdyfpo-2' })`
    display: table-cell;
    vertical-align: middle;
    padding: ${size(3)};
    max-width: 100vw;

    ${(props) => props.theme.breakpoint.LtSm} {
        vertical-align: bottom;
        padding: 0;
    }
`;

export const Dialog = styled.div.withConfig({ componentId: 'sc-qdyfpo-3' })`
    --dialog-gutter: ${size(4)};

    position: relative;
    width: 100%;
    background-color: var(--c-bg-main);
    color: var(--c-text-main);
    margin-left: auto;
    margin-right: auto;
    max-width: 700px;
    padding: var(--dialog-gutter);
    border-radius: ${size(0.5)};
    overflow: hidden;
    transform: translateY(${size(1)});
    animation: ${fadeIn} 300ms ease, ${noTransform} 300ms ease forwards;

    ${(props) => props.theme.breakpoint.LtSm} {
        --dialog-gutter: ${size(3)};

        transform: translateY(100%);
        animation: ${noTransform} 300ms ease forwards;
    }
`;

interface CloseBtnProps {
  $absolutePosition?: boolean;
  $disabledShapeOutside?: boolean;
}

export const CloseBtn = styled(ButtonReset).withConfig({ componentId: 'sc-qdyfpo-4' })<CloseBtnProps>`
    display: block;
    float: right;
    font-size: ${iconSize};
    margin-left: ${size(3)};
    margin-bottom: ${size(3)};
    margin-top: calc((${iconSize} - ${headingSize} * ${headingLineHeight}) / -2);
    border-radius: 50%;
    shape-outside: circle();
    transform-origin: 50% 50%;
    transition: transform 200ms ease;
    z-index: 1;

    &::before {
        content: "";
        position: absolute;
        top: 50%;
        left: 50%;
        width: 2.5em;
        height: 2.5em;
        border-radius: 50%;
        background-color: var(--c-bg-alt);
        opacity: 0;
        transform: translate(-50%, -50%);
        transition: opacity 200ms ease;
        z-index: -1;
    }

    &:active {
        transform: scale(0.9);
    }

    &:hover {
        &::before {
            opacity: 1;
        }
    }

    ${({ $disabledShapeOutside }) =>
      $disabledShapeOutside &&
      css`
            margin-bottom: 0;
            shape-outside: none;
        `}

    ${({ $absolutePosition }) =>
      $absolutePosition &&
      css`
            position: absolute;
            top: ${size(4)};
            right: ${size(4)};

            ${(props) => props.theme.breakpoint.LtSm} {
                top: ${size(3)};
                right: ${size(3)};
            }
        `}
`;

export const Illustration = styled.img.withConfig({ componentId: 'sc-qdyfpo-5' })`
    display: block;
    width: ${size(9)};
    height: ${size(9)};
    max-width: 100%;
    object-fit: contain;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: ${size(3)};
`;

export const Heading = styled.div.withConfig({ componentId: 'sc-qdyfpo-6' })`
    margin-bottom: ${size(3)};
    font-size: ${headingSize};
    font-weight: var(--font-weight-bold);
    line-height: ${headingLineHeight};
`;

export const Footer = styled.div.withConfig({ componentId: 'sc-qdyfpo-7' })`
    display: flex;
    align-items: center;
    margin-top: ${size(3)};

    ${(props) => props.theme.breakpoint.Sm} {
        justify-content: flex-end;
    }
`;

export const FooterItem = styled.div.withConfig({ componentId: 'sc-qdyfpo-8' })`
    flex-shrink: 0;

    ${(props) => props.theme.breakpoint.LtSm} {
        flex: 1 0 auto;
        width: 0px;
    }
`;

// Modal components (imported separatly)

export const ModalAccentSection = styled.div.withConfig({ componentId: 'sc-qdyfpo-9' })`
    padding: ${size(4)};
    margin: ${size(4)} ${size(-4)};
    background-color: var(--c-bg-alt);

    &:first-child {
        margin-top: ${size(-4)};
    }

    &:last-child {
        margin-bottom: ${size(-4)};
    }

    ${(props) => props.theme.breakpoint.LtSm} {
        padding: ${size(3)};
        margin: ${size(3)} ${size(-3)};

        &:first-child {
            margin-top: ${size(-3)};
        }

        &:last-child {
            margin-bottom: ${size(-3)};
        }
    }
`;
